/**
 * Demo Slide
 * The homepage's Demo section as one slide: three wishes, the app
 * answering each, and what made it better
 */

import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';
import { ENGINE_KINDS, ENGINE_ORDER, EngineIcon } from '@components/wish-story/engines';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { SlideCanvas } from '@components/press/pitch-deck/slide-canvas';
import { CanvasEyebrow, CanvasHeading } from '@components/press/pitch-deck/canvas-header';
import { KIND_NAMES } from '@components/press/pitch-deck/slides/deck-kinds';
import { DemoPhone, LIVE_DEMOS } from '@components/press/pitch-deck/slides/demo-phone';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * The homepage turns its demos one at a time; a slide sets them side by
 * side — the homepage's own (June, 2026-09-23: "see the demos from the
 * homepage"), its live ones: the same wishes and the same recordings, at
 * the homepage's 2x. Each arrives in turn — the wish, then its phone rising
 * into place, then what went into the answer — and its recording starts
 * once the phone has risen, playing on while the slide is up. What went
 * in is named and drawn as on How we build, the slide before, whose
 * equation — AI plus these three — it instances.
 *
 * Every phone shows its recording's first frame as a picture, the
 * recording playing over it (`DemoPhone`); still (reduced motion, or
 * `?capture=1` — how the deck is exported) the picture alone.
 */

/** When each column starts to arrive, in seconds after the slide does. */
const columnAt = (i: number) => 0.3 + i * 0.22;
/** How long a phone takes to rise; its recording starts as it lands. */
const RISE_S = 1.0;

export const DemoSlide: React.FC = () => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const from = (initial: TargetAndTransition): TargetAndTransition | false => (still ? false : initial);

  return (
    <SlideCanvas>
      <Header
        initial={from({ opacity: 0, y: 20 })}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE_OUT_EXPO }}
      >
        <CanvasEyebrow>Demo</CanvasEyebrow>
        <CanvasHeading>Wishes from Early Moms, Enhanced for You.</CanvasHeading>
      </Header>

      <Columns>
        {LIVE_DEMOS.map((demo, i) => (
          <Column key={demo.key}>
            <motion.div
              initial={from({ opacity: 0, y: 16 })}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE_OUT_EXPO, delay: columnAt(i) }}
            >
              <Name>{demo.title}</Name>
              <WishSpot>
                <Wish>“{demo.wish}”</Wish>
              </WishSpot>
            </motion.div>

            <PhoneSpot
              initial={from({ opacity: 0, y: 90 })}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: RISE_S, ease: EASE_OUT_EXPO, delay: columnAt(i) + 0.15 }}
            >
              <DemoPhone screen={demo.screen} startAfter={still ? null : columnAt(i) + 0.15 + RISE_S * 0.6} />
            </PhoneSpot>

            <Built>
              {ENGINE_ORDER.map((kind, k) => (
                <BuiltRow
                  key={kind}
                  initial={from({ opacity: 0, y: 10 })}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: columnAt(i) + 0.7 + k * 0.1 }}
                >
                  <Pill style={{ background: ENGINE_KINDS[kind].pill, color: ENGINE_KINDS[kind].pillInk }}>
                    <EngineIcon engine={kind} size={14} color={ENGINE_KINDS[kind].pillInk} />
                    {KIND_NAMES[kind]}
                  </Pill>
                  <BuiltText>{demo.built[kind]}</BuiltText>
                </BuiltRow>
              ))}
            </Built>
          </Column>
        ))}
      </Columns>
    </SlideCanvas>
  );
};

/* Canvas pixels (see SlideCanvas): the header across the top, then three
   columns whose phones stand level with one another. */

const Header = styled(motion.div)`
  position: absolute;
  left: 150px;
  top: 76px;
`;

const Columns = styled.div`
  position: absolute;
  left: 150px;
  right: 150px;
  top: 222px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 90px;
`;

const Column = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

/* The product, as a label over the wish. */
const Name = styled.h3`
  font-family: ${typography.fontFamily.small};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin: 0 0 14px;
`;

/* Room for a two-line wish, the bubble at its foot, so a one-line wish
   leaves every phone level with the others. */
const WishSpot = styled.div`
  height: 100px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

/* The wish as a mom writes it in the app: its coral glass bubble. */
const Wish = styled.p`
  max-width: 440px;
  margin: 0;
  padding: 12px 22px;
  border-radius: 22px 22px 6px 22px;
  border: 1px solid ${colors.chat.glassRim};
  background: linear-gradient(180deg, ${colors.chat.userGlassTop}, ${colors.chat.userGlassBottom});
  box-shadow: 0 10px 24px ${colors.shadowSoft};
  color: ${colors.neutral.extraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 26px;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -0.01em;
  text-wrap: balance;
`;

/* Where the phone rises to. Inside it the phone's foot fades away before
   the app's tab bar, as on the homepage (`FadingPhone`). */
const PhoneSpot = styled(motion.div)`
  margin-top: 26px;
`;

/* What went into the answer: the three kinds in How we build's order,
   names and colours, over the phone's faded foot. */
const Built = styled.div`
  margin-top: -52px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 440px;
`;

const BuiltRow = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 13px 4px 10px;
  border-radius: 9999px;
  font-family: ${typography.fontFamily.small};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.5;
  text-transform: uppercase;
  white-space: nowrap;
`;

const BuiltText = styled.span`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  line-height: 1.35;
  color: ${colors.neutral.darkMedium};
  text-wrap: balance;
`;
